import { isEmpty } from 'lodash';
import {
  ViewTemplateLayoutImage,
  getLayoutMediaFiles,
} from '@shared/scenes/workflows/viewTemplateLayoutItems';
import { FileUpload } from '@shared/types/fieldValues';
import { useImageSrcAndDimensions } from '@shared/util/hooks';
import styles from './layoutItems.module.css';

/**
 * The images of an image layout item, stacked at their own size.
 *
 * Bounded rather than stretched: an image narrower than the column stays narrow instead of being
 * blown up to fit, and one wider than the column is brought down to it. `.media` carries both
 * caps — the same treatment the builder's layout row gives the same file.
 */
export function LayoutImageItem({ item }: { item: ViewTemplateLayoutImage }) {
  const files = getLayoutMediaFiles(item);

  if (isEmpty(files)) {
    return null;
  }

  return (
    <div className={`${styles.item} ${styles.images}`}>
      {files.map(file => (
        <LayoutImage key={file.url} file={file} />
      ))}
    </div>
  );
}

/**
 * One image, sized through the hook every other image in the app goes through — it asks imgix
 * for a file that fits, corrects dimensions for EXIF orientation, and reports when the browser
 * has resolved it.
 */
function LayoutImage({ file }: { file: FileUpload }) {
  const { src } = useImageSrcAndDimensions({
    image: file,
    containerWidth: IMAGE_BOUNDS[0],
    containerHeight: IMAGE_BOUNDS[1],
    size: 'contain',
  });

  return <img src={src} alt={file.name} className={styles.media} />;
}

/**
 * What imgix is asked to fit inside. Twice the form column's 456px so an image large enough to
 * fill the column is still sharp on a high-density screen, and square so a tall image is bounded
 * by the same number as a wide one rather than being fetched at its full height.
 *
 * imgix never scales up, so a file smaller than this comes back at its own size and renders at
 * it — which is what `.media`'s `width: auto` is there to keep.
 */
const IMAGE_BOUNDS: [number, number] = [912, 912];
